import React, { useEffect, useState } from 'react';
import { ArrowRight, Check } from 'lucide-react';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import {
  getPlans,
  createSubscription,
  getMatrimonyUsers,
  getUserSubscriptions
} from '../utils/api';
import { ClockLoader } from 'react-spinners';
import { initializePayment } from '../utils/paymentUtils';
import { PaymentType } from '../utils/paymentTypes';

// Helper function to convert days to human-readable format
const formatTenure = (days) => {
  if (days === 30) return '1 Month';
  if (days === 90) return '3 Months';
  if (days === 180) return '6 Months';
  if (days === 365) return '1 Year';
  return `${days} days`; // fallback
};

export default function MatrimonyPremiumPricing() {
  const [plans, setPlans] = useState([]);
  const [activePlanId, setActivePlanId] = useState(null);
  const [loadingPlans, setLoadingPlans] = useState(true);
  const [processingPlanId, setProcessingPlanId] = useState(null);
  const [userType, setUserType] = useState('non-entrepreneur');
  const [userSubscriptions, setUserSubscriptions] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchUserSubscriptions = async () => {
      try {
        const token = localStorage.getItem('matrimonyToken');
        if (!token) {
          console.log('No token found, skipping subscription fetch');
          return;
        }

        console.log('Fetching user subscriptions...');
        const subscriptions = await getUserSubscriptions(token);
        console.log('Raw subscriptions data:', subscriptions);

        const paidSubscriptions = subscriptions.filter(sub =>
          sub.paymentStatus === 'Paid' || sub.payment_status === 'Paid'
        );
        console.log('Paid subscriptions:', paidSubscriptions);

        setUserSubscriptions(paidSubscriptions);

        if (paidSubscriptions.length > 0) {
          const latestSubscription = paidSubscriptions.reduce((latest, current) => {
            const currentDate = new Date(current.createdAt || current.created_at);
            const latestDate = new Date(latest.createdAt || latest.created_at);
            return currentDate > latestDate ? current : latest;
          });

          console.log('Latest subscription:', latestSubscription);
          const activeId = latestSubscription.id || latestSubscription._id || latestSubscription.membership_id;
          console.log('Setting active plan ID:', activeId);
          setActivePlanId(activeId);
        } else {
          console.log('No paid subscriptions found');
        }
      } catch (error) {
        console.error('Failed to fetch user subscriptions:', error);
      }
    };

    const init = async () => {
      try {
        // Fetch users
        const usersResp = await getMatrimonyUsers();
        const usersList = Array.isArray(usersResp)
          ? usersResp
          : Array.isArray(usersResp?.data)
            ? usersResp.data
            : [];
        const userEmail = localStorage.getItem('matrimonyUserEmail');
        const currentUser = usersList.find((u) => u.email === userEmail) || {};
        const isEntrepreneur = Boolean(currentUser.founder_id || currentUser.startupId);
        setUserType(isEntrepreneur ? 'entrepreneur' : 'non-entrepreneur');

        // Fetch plans
        const plansResp = await getPlans();
        const allPlans = Array.isArray(plansResp)
          ? plansResp
          : Array.isArray(plansResp?.data)
            ? plansResp.data
            : [];
        const matrimonyPlans = allPlans.filter((p) => p.type === 'Matrimony');
        const availablePlans = matrimonyPlans.filter((p) =>
          isEntrepreneur
            ? p.matrimonyType === 'Entrepreneur'
            : p.matrimonyType === 'Non-entrepreneur'
        );
        const sanitizedPlans = availablePlans.map((p) => ({
          id: p._id,
          name: p.title,
          price: p.price,
          features: Array.isArray(p.features) && p.features.length > 0
            ? p.features
            : ['Basic features included'],
          tenure: p.tenure,
          matrimonyType: p.matrimonyType,
          type: p.type,
          formattedTenure: formatTenure(p.tenure),
        }));
        setPlans(sanitizedPlans);

        // Fetch user subscriptions after plans are loaded
        await fetchUserSubscriptions();
      } catch (err) {
        toast.error(err.response?.data?.message || err.message || 'Failed to load plans');
      } finally {
        setLoadingPlans(false);
      }
    };

    init();
  }, []);

  const isPlanActive = (planId) => {
    const result = userSubscriptions.some(sub => {
      const idMatch = sub.id === planId || sub._id === planId || sub.membership_id === planId;
      const paidStatus = sub.paymentStatus === 'Paid' || sub.payment_status === 'Paid';

      console.log(`Checking subscription ${sub.id || sub._id || sub.membership_id}:`, {
        planId,
        subId: sub.id,
        sub_id: sub._id,
        subMembershipId: sub.membership_id,
        idMatch,
        paidStatus,
        isActive: idMatch && paidStatus
      });

      return idMatch && paidStatus;
    });

    console.log(`isPlanActive result for ${planId}:`, result);
    return result;
  };

  const handleGetStarted = async (plan) => {
    if (isPlanActive(plan.id)) {
      return;
    }

    setProcessingPlanId(plan.id);
    try {
      const token = localStorage.getItem('matrimonyToken');
      if (!token) {
        toast.error('Please login to continue');
        return navigate('/matrimony/login');
      }

      // Store plan data in sessionStorage for callback
      sessionStorage.setItem(
        'subscription_plan',
        JSON.stringify(plan)
      );

      // Initialize payment
      await initializePayment({
        type: PaymentType.MATRIMONY,
        amount: plan.price,
        token,
        returnUrl: `${window.location.origin}/payment-callback`
      });

    } catch (error) {
      console.error('Payment Error:', error);
      toast.error(error.message || 'Payment processing failed');
    } finally {
      setProcessingPlanId(null);
    }
  };

  if (loadingPlans) {
    return (
      <div className="flex justify-center items-center py-10">
        <ClockLoader size={50} />
      </div>
    );
  }

  return (
    <div className="bg-[#f9f6f6] py-20 px-4">
      <h2 className="text-3xl md:text-4xl font-semibold text-center mb-12">
        <span className="text-black">Pricing</span>
      </h2>
      <div className="flex flex-col md:flex-row justify-center gap-8 items-center">
        {plans.length === 0 ? (
          <div className="text-center p-8 bg-white rounded-lg shadow max-w-md mx-auto">
            <h3 className="text-xl font-semibold text-gray-800 mb-2">No Plans Available</h3>
            <div className="text-left bg-gray-100 p-4 rounded mb-4">
              <p className="font-medium">Debug Info:</p>
              <p>User Email: {localStorage.getItem('matrimonyUserEmail') || 'Not found'}</p>
              <p>User Type: {userType}</p>
              <p>Total Plans: {plans.length}</p>
            </div>
            <button
              onClick={() => window.location.reload()}
              className="px-4 py-2 bg-indigo-600 text-white rounded hover:bg-indigo-700"
            >
              Refresh
            </button>
          </div>
        ) : (
          plans.map((plan) => {
            const isActive = isPlanActive(plan.id);
            const isProcessing = processingPlanId === plan.id;

            return (
              <div
                key={plan.id}
                className="rounded-3xl p-8 shadow-md w-full max-w-sm text-center bg-white"
              >
                <div className="mb-4">
                  <span className="inline-block px-3 py-1 text-xs font-semibold rounded-full bg-indigo-100 text-indigo-800">
                    {plan.matrimonyType}
                  </span>
                </div>

                <h3 className="text-xl font-semibold text-gray-800 mb-2">{plan.name}</h3>

                <div className="text-4xl font-bold text-indigo-900 mb-6">
                  ₹{plan.price}{' '}
                  <span className="text-base font-medium text-gray-500">/ {plan.formattedTenure}</span>
                </div>

                <button
                  onClick={() => handleGetStarted(plan)}
                  className={`flex items-center justify-center gap-2 mx-auto mb-8 rounded-full px-6 py-3 w-full transition-all ${isActive || isProcessing
                    ? 'bg-gray-200 text-gray-500 cursor-not-allowed'
                    : 'bg-gradient-to-r from-[#E25B46] via-[#B9398E] to-[#E25B46] text-white hover:shadow-lg'
                    }`}
                  disabled={isActive || isProcessing}
                >
                  {isProcessing ? (
                    <>
                      <ClockLoader size={16} color="#6b7280" />
                      <span>Processing...</span>
                    </>
                  ) : isActive ? (
                    <>
                      <Check size={16} />
                      <span>Current Plan</span>
                    </>
                  ) : (
                    <>
                      <span>Get Started</span>
                      <ArrowRight size={18} />
                    </>
                  )}
                </button>

                <div className="text-left border-t pt-4">
                  <h4 className="font-semibold text-sm text-gray-800 mb-3">Included Features:</h4>
                  <ul className="space-y-2">
                    {plan.features.map((feat, idx) => (
                      <li key={idx} className="flex items-start text-sm text-gray-700">
                        <Check size={16} className="text-green-500 mt-0.5 mr-2 flex-shrink-0" />
                        <span>{feat}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}